import type { RecordValue, Schema, Value } from "./types.ts";
export function invalidDateOrder(body: RecordValue): boolean {
  return typeof body.startsAtEpochMillis === "number" && typeof body.endsAtEpochMillis === "number"
    && body.endsAtEpochMillis <= body.startsAtEpochMillis;
}
export function contextualSchema(key: string, schema: Schema, parent: RecordValue): Schema {
  if (key !== "criteria" || !schema.items?.properties?.weightPoints) return schema;
  const points = parent.scoringMode === "MAX_RATIO_POINTS";
  const properties = { ...schema.items.properties };
  delete properties[points ? "weightBps" : "weightPoints"];
  return { ...schema, items: { ...schema.items, properties,
    required: [...(schema.items.required || []).filter(k => !["weightBps", "weightPoints"].includes(k)), points ? "weightPoints" : "weightBps"] } };
}
export function relevant(key: string, parent: RecordValue): boolean {
  if (key === "publicVoteWeight") return ["RATING", "QUESTIONNAIRE"].includes(String(parent.inputMethod)) && parent.relationshipSource !== "PUBLIC";
  if (key === "roleCode" && "weightBps" in parent && "roundId" in parent) return parent.channel === "EVALUATION";
  if (key === "scopeId") return parent.scopeMode === "EXPLICIT";
  if (key === "options") return parent.type === "CHOICE";
  if (key === "min" || key === "max") return parent.type === "NUMBER";
  if (key === "entryIds" && "participantSetPolicy" in parent)
    return parent.participantSetPolicy === "EXPLICIT";
  if (
    ["opensAtEpochMillis", "closesAtEpochMillis"].includes(key) &&
    "mode" in parent
  )
    return parent.mode === "SCHEDULED";
  if (["roleCodes", "closesAtEpochMillis"].includes(key) && "state" in parent)
    return parent.state === "OPEN";
  if (key === "displayOrder" && "included" in parent)
    return parent.included === true;
  return true;
}
export function conditionalRequired(key: string, parent: RecordValue): boolean {
  return (
    (key === "scopeId" && parent.scopeMode === "EXPLICIT") ||
    (["min", "max"].includes(key) && parent.type === "NUMBER") ||
    (key === "options" && parent.type === "CHOICE") ||
    (key === "entryIds" && parent.participantSetPolicy === "EXPLICIT") ||
    (["opensAtEpochMillis", "closesAtEpochMillis"].includes(key) &&
      parent.mode === "SCHEDULED") ||
    (["roleCodes", "closesAtEpochMillis"].includes(key) &&
      parent.state === "OPEN") ||
    (key === "displayOrder" && parent.included === true)
  );
}
export function clean(schema: Schema, value: Value): Value {
  if (schema.type === "array" && Array.isArray(value))
    return value.map((v) => clean(schema.items!, v));
  if (
    schema.type !== "object" ||
    !value ||
    Array.isArray(value) ||
    typeof value !== "object"
  )
    return value;
  return Object.fromEntries(
    Object.entries(schema.properties || {})
      .filter(
        ([key]) =>
          relevant(key, value) && value[key] !== undefined && value[key] !== "",
      )
      .map(([key, child]) => [key, clean(contextualSchema(key, child, value), value[key])]),
  );
}
export function validate(
  schema: Schema,
  value: Value | undefined,
  required = false,
  path = "",
): string[] {
  if (value === undefined || value === "" || value === null)
    return required && !(value === null && schema.nullable) ? [path] : [];
  if (schema.enum && !schema.enum.includes(value as string | boolean))
    return [path];
  if (schema.type === "object") {
    if (typeof value !== "object" || Array.isArray(value)) return [path];
    return Object.entries(schema.properties || {})
      .filter(([k]) => relevant(k, value))
      .flatMap(([k, s]) =>
        validate(
          contextualSchema(k, s, value),
          value[k],
          schema.required?.includes(k) || conditionalRequired(k, value),
          path ? `${path}.${k}` : k,
        ),
      );
  }
  if (schema.type === "array") {
    if (
      !Array.isArray(value) ||
      value.length < (schema.minItems || 0) ||
      value.length > (schema.maxItems ?? Infinity)
    )
      return [path];
    return value.flatMap((v, i) =>
      validate(schema.items!, v, true, `${path}.${i + 1}`),
    );
  }
  if (schema.type === "integer")
    return typeof value === "number" &&
      Number.isSafeInteger(value) &&
      value >= (schema.minimum ?? -Infinity) &&
      value <= (schema.maximum ?? Infinity)
      ? []
      : [path];
  if (schema.type === "boolean")
    return typeof value === "boolean" ? [] : [path];
  if (schema.type === "string")
    return typeof value === "string" &&
      value.trim().length >= (schema.minLength || 0) &&
      value.length <= (schema.maxLength ?? Infinity) &&
      (!schema.pattern || new RegExp(schema.pattern).test(value))
      ? []
      : [path];
  return [];
}
export function policyIssues(body: RecordValue): string[] {
  const issues: string[] = invalidDateOrder(body) ? ["endsAtEpochMillis"] : [];
  for (const key of ["sources", "criteria"])
    if (Array.isArray(body[key])) {
      const rows = body[key] as RecordValue[];
      const points = key === "criteria" && body.scoringMode === "MAX_RATIO_POINTS";
      if (!points && rows.reduce((sum, r) => sum + Number(r.weightBps || 0), 0) !== 10000)
        issues.push(`${key}.weightBps`);
      if (points && rows.some(r => !Number.isSafeInteger(r.weightPoints) || Number(r.weightPoints) < 1 || Number(r.weightPoints) > 10000))
        issues.push("criteria.weightPoints");
      if (points && rows.some(r => r.type === "NUMBER" && (Number(r.min) < 0 || Number(r.max) <= 0)))
        issues.push("criteria.min");
      const ids = rows.map((r) => key === "sources" ? `${r.roundId}/${r.channel || "ALL"}/${r.roleCode || ""}` : r.id);
      if (key === "sources" && rows.some(r => r.roleCode && r.channel !== "EVALUATION")) issues.push("sources.roleCode");
      if (new Set(ids).size !== ids.length) issues.push(key);
      for (const row of rows)
        if (row.type === "NUMBER" && Number(row.max) <= Number(row.min))
          issues.push("criteria.max");
    }
  if (
    body.mode === "SCHEDULED" &&
    Number(body.closesAtEpochMillis) <= Number(body.opensAtEpochMillis)
  )
    issues.push("closesAtEpochMillis");
  if (Array.isArray(body.roleRules)) {
    const rows = body.roleRules as RecordValue[];
    if (new Set(rows.map((r) => r.roleCode)).size !== rows.length)
      issues.push("roleRules.roleCode");
    if (
      body.roleConflictPolicy === "PRIORITY" &&
      new Set(rows.map((r) => r.priority)).size !== rows.length
    )
      issues.push("roleRules.priority");
    const audienceRoles = rows.filter(r => r.publicVoteWeight !== undefined);
    if (audienceRoles.some(r => r.inputMethod === "DIRECT" || r.relationshipSource === "PUBLIC" ||
      !Number.isSafeInteger(r.publicVoteWeight) || Number(r.publicVoteWeight) < 1 || Number(r.publicVoteWeight) > 1000000) ||
      (audienceRoles.length > 0 && body.roleConflictPolicy !== "HIGHEST_WEIGHT_PER_CHANNEL"))
      issues.push("roleRules.publicVoteWeight");
    const contextIds = [...rows.map(r => String(r.roleCode)), ...audienceRoles.map(r => `${r.roleCode}_PUBLIC`)];
    if (new Set(contextIds).size !== contextIds.length || contextIds.some(id => id.length > 96))
      issues.push("roleRules.roleCode");
    if (rows.some((r) => r.requiresActiveRelationship !== true))
      issues.push("roleRules.requiresActiveRelationship");
    if (
      rows.some(
        (r) => r.inputMethod === "DIRECT" && r.requiresPresentation === true,
      )
    )
      issues.push("roleRules.requiresPresentation");
    if (
      body.normalizationPolicy === "DIRECT_SHARE" &&
      rows.some((r) => r.inputMethod !== "DIRECT")
    )
      issues.push("normalizationPolicy");
  }
  return issues;
}
